import _ from 'lodash';
import { useQueryClient } from 'react-query';
import { useToastify } from '../../../../../../Hooks';
import useFormServerErrors from '../../../../../../Hooks/useFormServerErrors';
import {
    useKendoGrid,
    useFrontendKendoGridDataHandler,
    useKendoGridInlineEditing,
} from '../../../../../../Hooks/useKendoGrid';
import { trimStrings } from '../../../../../Licenses/licenseHelper';
import { useCreateDGManagement, useDeleteDGManagement, useUpdateDGManagement } from '../../../../hooks/useDGManagement';

const idField = 'id';
const editField = 'inEdit';

const useDGManagement = (dgs: any) => {
    const queryClient = useQueryClient();
    const { gridState, gridResult, setGridState, changeGridResult } = useKendoGrid({
        source: dgs as any[],
    });
    const { showToastError, showToastSuccess } = useToastify();
    const handleDataStateChange = useFrontendKendoGridDataHandler({
        setGridState,
        changeGridResult,
        data: dgs as any[],
    });

    const { addNew, add, discard, enterEdit, update, cancel, remove, handleItemChange } = useKendoGridInlineEditing({
        idField,
        editField,
        source: dgs as any[],
        data: gridResult.data,
        setData: changeGridResult,
    });

    const createDGManagementMutation = useCreateDGManagement();
    const deleteDGManagementMutation = useDeleteDGManagement();
    const updateDGManagementMutation = useUpdateDGManagement();
    const { mutate: createDGManagement } = createDGManagementMutation;
    const { mutate: deleteDGManagement } = deleteDGManagementMutation;
    const { mutate: updateDGManagement } = updateDGManagementMutation;
    const { hasError, getCustomErrorMessageProductReservation } = useFormServerErrors();
    const addItem = (dataItem: any) => {
        const model = { ...dataItem };
        delete model?.id;
        trimStrings(model);
        createDGManagement(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Project added!');
                    add(dataItem);
                    queryClient.invalidateQueries(['deelproject']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const deleteItem = (dataItem: any) => {
        deleteDGManagement(dataItem[idField], {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Project deleted!');
                    remove(dataItem);
                    queryClient.invalidateQueries(['deelproject']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const updateItem = (dataItem: any) => {
        delete dataItem.inEdit;
        const model = { ...dataItem };
        trimStrings(model);
        updateDGManagement(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess("Project's data updated!");
                    update(dataItem);
                    queryClient.invalidateQueries(['deelproject']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
                cancel(dataItem);
            },
        });
    };
    const validate = (dataItem: any) => {
        if (_.isEmpty(dataItem?.name?.trim())) {
            return false;
        }
        return true;
    };

    return {
        idField,
        editField,
        gridState,
        gridResult,
        setGridState,
        changeGridResult,
        handleDataStateChange,
        handleItemChange,
        validate,
        inlineEditing: { addNew, add: addItem, discard, enterEdit, update: updateItem, cancel, remove: deleteItem },
        mutations: {
            createDGManagementMutation,
            deleteDGManagementMutation,
        },
    };
};

export default useDGManagement;
